import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal, computed } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { UserAccessResponse, LibraryAccess, ClassAccess } from '@shared/models/dto/access';

@Injectable({
  providedIn: 'root'
})
export class UserAccessService {
  private http = inject(HttpClient);
  private apiUrl = environment.backendHost;

  // State
  private _userAccess = signal<LibraryAccess[]>([]);
  private _selectedLibraryId = signal<string>('');
  private _selectedClassId = signal<string>('');
  private _isLoading = signal<boolean>(false);
  private _error = signal<string | null>(null);

  // Public computed signals
  readonly userAccess = computed(() => this._userAccess());
  readonly selectedLibraryId = computed(() => this._selectedLibraryId());
  readonly selectedClassId = computed(() => this._selectedClassId());
  readonly isLoading = computed(() => this._isLoading());
  readonly error = computed(() => this._error());

  // Current selection info
  readonly currentSelection = computed(() => {
    const libraryId = this._selectedLibraryId();
    const classId = this._selectedClassId();
    const library = this._userAccess().find(lib => lib.libraryId === libraryId);
    const classInfo = library?.classes?.find(cls => cls.classId === classId);

    return {
      libraryId,
      classId,
      libraryName: library?.libraryName ?? '',
      className: classInfo?.className ?? ''
    };
  });

  // Check if a valid selection exists
  readonly hasValidSelection = computed(() => {
    return !!this._selectedLibraryId() && !!this._selectedClassId();
  });

  /**
   * Load user access (available classes and libraries)
   */
  loadUserAccess(): void {
    this._isLoading.set(true);
    this._error.set(null);

    this.http.get<UserAccessResponse>(`${this.apiUrl}/api/v1/document/user/access`)
      .subscribe({
        next: (response) => {
          this._userAccess.set(response.access ?? []);
          this._isLoading.set(false);
        },
        error: (err) => {
          this._error.set('Failed to load user access. Please try again.');
          this._isLoading.set(false);
          console.error('[UserAccessService] Error loading user access:', err);
        }
      });
  }

  /**
   * Set the selected library and class combination
   */
  setSelection(libraryId: string, classId: string): void {
    const changed = this._selectedLibraryId() !== libraryId || this._selectedClassId() !== classId;
    
    this._selectedLibraryId.set(libraryId);
    this._selectedClassId.set(classId);
    
    if (changed) {
      console.log('[UserAccessService] Selection changed:', { libraryId, classId });
    }
  }

  /**
   * Get available classes for a specific library
   */
  getClassesForLibrary(libraryId: string): ClassAccess[] {
    const library = this._userAccess().find(lib => lib.libraryId === libraryId);
    return library?.classes ?? [];
  }

  /**
   * Check if a library/class combination is valid
   */
  isValidSelection(libraryId: string, classId: string): boolean {
    const library = this._userAccess().find(lib => lib.libraryId === libraryId);
    if (!library || !library.classes) return false;
    
    return library.classes.some(cls => cls.classId === classId);
  }

  /**
   * Reset the service state
   */
  reset(): void {
    this._userAccess.set([]);
    this._selectedLibraryId.set('');
    this._selectedClassId.set('');
    this._isLoading.set(false);
    this._error.set(null);
  }
}